/* eslint-disable react-hooks/exhaustive-deps */
import { useTranslation } from 'react-i18next';
import {
  useFetchReportList,
  useDownloadPresignedUrl,
  useFetchCustomReportList,
  useMonthlyReportGeneration,
  useCustomerMonthlyPdf,
} from './Hook/Index';
import { useOperatorStore } from '../../store/useOperatorStore';
import TouRatesHint from '../tou-pricing/TouRatesHint';
import { useLocation } from 'react-router-dom';
import {
  Card,
  Row,
  Col,
  Tabs,
  DatePicker,
  Table,
  Button,
  Grid,
  Tag,
  Empty,
  Select,
  Drawer,
  Form,
  Input,
  InputNumber,
  message,
  Upload,
} from 'antd';
import {
  UserOutlined,
  ShopOutlined,
  ThunderboltOutlined,
  CloseCircleOutlined,
  CheckCircleOutlined,
  DownloadOutlined,
  DollarOutlined,
  FileTextOutlined,
  ClockCircleOutlined,
  SyncOutlined,
  ReloadOutlined,
  PlusOutlined,
  LineChartOutlined,
  TeamOutlined,
  PoweroffOutlined,
  UserSwitchOutlined,
  UploadOutlined,
} from '@ant-design/icons';
import { useState, useMemo, useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { formatKwhFromWatts, formatReportNumber } from '../../utils/CommonFunctions';
import { useAuthStore } from '../../store/useAuthStore';
import { useFilUpload } from '../../Hooks/Index';
import { DebouncedSelect, fetchLocationList, fetchCustomerList } from '../../components/useDebouncedSelectLocation';
import ChargePointCountTab from './ChargePointCountTab';
import ChargingSessionTab from './ChargingSessionTab';
import EnergyConsumptionTab from './EnergyConsumptionTab';
import ChargerOnlineTimeTab from './ChargerOnlineTimeTab';
import UserCountTab from './UserCountTab';
import UserAnalysisTab from './UserAnalysisTab';
import RevenueTab from './RevenueTab';
import CustomerDrawerView from '../../components/CustomerDrawerView';
import { Tooltip } from 'antd';
import { getErrorMessage } from '../../utils/errorHandler';
const { MonthPicker, RangePicker } = DatePicker;
const { useBreakpoint } = Grid;
const { Option } = Select;

export default function ReportPage() {
  const { t } = useTranslation('reportPage');
  const { user } = useAuthStore();
  const { operatorId, name: operatorName } = useOperatorStore();
  const location = useLocation();
  const queryClient = useQueryClient();
  const [createReportDrawerOpen, setCreateReportDrawerOpen] = useState(false);
  const [createReportForm] = Form.useForm();
  const [createReportBillFileList, setCreateReportBillFileList] = useState([]);
  const [createReportUploadedBillUrl, setCreateReportUploadedBillUrl] = useState('');
  const [customerDrawerOpen, setCustomerDrawerOpen] = useState(false);
  const [selectedCustomerIdForDrawer, setSelectedCustomerIdForDrawer] = useState(null);
  const screens = useBreakpoint();
  const isMobile = !screens.md;
  const [selectedRole, setSelectedRole] = useState(() => {
    if (user?.role === 'LOCATION_OWNER') return 'LOCATION_OWNER';
    if (['SUPER_ADMIN', 'ADMIN', 'OPERATOR'].includes(user?.role)) return 'OPERATOR';
    return user?.role || 'CUSTOMER';
  });
  const [reportLocationId, setReportLocationId] = useState(null);
  const [reportCustomerId, setReportCustomerId] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState(dayjs().format('YYYY-MM'));
  const [activeMainTab, setActiveMainTab] = useState(
    location.state?.activeMainTab || (user?.role === 'LOCATION_OWNER' ? 'reports' : 'otherReports')
  );
  const [activeReportTab, setActiveReportTab] = useState(() => {
    if (user?.role === 'LOCATION_OWNER') return 'LOCATION_OWNER';
    if (['SUPER_ADMIN', 'ADMIN', 'OPERATOR'].includes(user?.role)) return 'OPERATOR';
    return user?.role || 'CUSTOMER';
  });
  const [activeOtherReportTab, setActiveOtherReportTab] = useState(
    location.state?.activeOtherReportTab || 'chargePointCount'
  );
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const [customReportPagination, setCustomReportPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });

  // Custom Report Filters
  const [customReportDateRange, setCustomReportDateRange] = useState([]);
  const [customReportStatus, setCustomReportStatus] = useState(undefined);
  const [customReportTemplate, setCustomReportTemplate] = useState(undefined);
  const [downloadingCustomReportId, setDownloadingCustomReportId] = useState(null);

  const customReportPayload = useMemo(() => ({
    ...(operatorId && { operatorId }),
    offset: customReportPagination.current,
    limit: customReportPagination.pageSize,
    page: customReportPagination.current,
    ...(customReportDateRange?.[0] && { fromDate: customReportDateRange[0].toISOString() }),
    ...(customReportDateRange?.[1] && { toDate: customReportDateRange[1].toISOString() }),
    ...(customReportStatus && { status: customReportStatus }),
    ...(customReportTemplate && { reportTemplate: customReportTemplate }),
  }), [operatorId, customReportPagination, customReportDateRange, customReportStatus, customReportTemplate]);

  const { mutate: DownloadFile } = useDownloadPresignedUrl();
  const { mutateAsync: uploadFile } = useFilUpload();
  const { mutate: generateMonthlyReport, isPending: isGeneratingReport } = useMonthlyReportGeneration({
    onSuccess: () => {
      message.success(t('createReportSuccess'));
      setCreateReportDrawerOpen(false);
      createReportForm.resetFields();
      setCreateReportBillFileList([]);
      setCreateReportUploadedBillUrl('');
      queryClient.invalidateQueries({ queryKey: ['REPORT_LIST'] });
    },
    onError: (err) => {
      message.error(err?.response?.data?.message || t('somethingWentWrong'));
    },
  });

  const { mutate: generateCustomerPdf, isPending: isGeneratingCustomerPdf } = useCustomerMonthlyPdf({
    onSuccess: () => {
      message.success(t('customerPdfSuccess'));
      queryClient.invalidateQueries({ queryKey: ['REPORT_LIST'] });
    },
    onError: (err) => {
      message.error(err?.response?.data?.message || t('somethingWentWrong'));
    },
  });

  const handleGenerateCustomerPdf = () => {
    if (!reportCustomerId) {
      message.warning(t('selectCustomerFirst'));
      return;
    }
    if (!selectedMonth) {
      message.warning(t('selectMonthFirst'));
      return;
    }
    const m = dayjs(selectedMonth);
    generateCustomerPdf({
      customerId: reportCustomerId,
      year: m.year(),
      month: m.month() + 1,
    });
  };

  const getMonthRange = (month) => ({
    fromDate: dayjs(month).startOf('month').format('YYYY-MM-DD'),
    toDate: dayjs(month).endOf('month').format('YYYY-MM-DD'),
  });

  const { data: reportList, isLoading: isReportListLoading } =
    useFetchReportList(
      {
        role: selectedRole,
        ...(selectedRole !== 'SUPER_ADMIN' && { operatorId }),
        ...(selectedMonth && getMonthRange(selectedMonth)),
        ...(selectedRole === 'LOCATION_OWNER' && reportLocationId && { locationId: reportLocationId }),
        ...(selectedRole === 'CUSTOMER' && reportCustomerId && { customerId: reportCustomerId }),
        offset: pagination.current,
        limit: pagination.pageSize,
      },
      {
        enabled:
          activeMainTab === 'reports' &&
          Object.keys(selectedMonth || {}).length > 0,
      }
    );
  const { data: customReportList, isLoading: isCustomReportListLoading, refetch: refetchCustomReportList } =
    useFetchCustomReportList(
      customReportPayload,
      {
        enabled: activeMainTab === 'customReports',
      }
    );

  const handleMainTabChange = (key) => {
    setActiveMainTab(key);
  };

  const handleReportsTabChange = (key) => {
    setSelectedRole(key);
    setActiveReportTab(key);
    setPagination((prev) => ({ ...prev, current: 1 }));
    if (key !== 'LOCATION_OWNER') setReportLocationId(null);
    if (key !== 'CUSTOMER') setReportCustomerId(null);
  };

  const handleReportLocationChange = (value) => {
    setReportLocationId(value || null);
    setPagination((prev) => ({ ...prev, current: 1 }));
  };

  const handleOtherReportsTabChange = (key) => {
    setActiveOtherReportTab(key);
  };

  const handleMonthChange = (date, dateString) => { setSelectedMonth(dateString || (date && dayjs(date).format('YYYY-MM'))) };

  const handleTableChange = (pagination) => setPagination(pagination);

  const handleOpenCreateReport = () => {
    createReportForm.setFieldsValue({
      perUnitPrice: undefined,
      fullBillAmount: undefined,
      totalConsumption: undefined,
    });
    setCreateReportBillFileList([]);
    setCreateReportUploadedBillUrl('');
    setCreateReportDrawerOpen(true);
  };

  const handleCloseCreateReportDrawer = () => {
    setCreateReportDrawerOpen(false);
    createReportForm.resetFields();
    setCreateReportBillFileList([]);
    setCreateReportUploadedBillUrl('');
  };

  const handleBillFileUpload = async ({ file, onSuccess, onError }) => {
    try {
      const url = await uploadFile(file);
      const resolvedUrl = typeof url === 'string' ? url : url?.url ?? url?.Location;
      setCreateReportUploadedBillUrl(resolvedUrl);
      setCreateReportBillFileList((prev) =>
        prev.filter((f) => f.uid !== file.uid).concat({ uid: file.uid, name: file.name, status: 'done', url: resolvedUrl })
      );
      onSuccess(resolvedUrl, file);
      message.success(t('billFileUploadSuccess'));
    } catch (err) {
      onError(err);
      message.error(err?.response?.data?.message || t('billFileUploadFailed'));
    }
  };

  const handleCreateReportSubmit = (values) => {
    const resolvedOperatorId = values.operatorId || operatorId;
    if (!resolvedOperatorId) {
      message.warning(t('selectOperatorFirst'));
      return;
    }
    const payload = {
      operatorId: resolvedOperatorId,
      fullBillAmount: values.fullBillAmount,
      totalConsumption: values.totalConsumption,
    };
    // Optional override only — TOU rates are used by default on the backend
    if (values.perUnitPrice != null && values.perUnitPrice !== '') {
      payload.perUnitPrice = values.perUnitPrice;
    }
    if (createReportUploadedBillUrl) {
      payload.billFileUrl = createReportUploadedBillUrl;
    }
    generateMonthlyReport(payload);
  };


  const handleDownloadReport = (fileUrl, reportName) => {
    DownloadFile(
      { fileUrl },
      {
        onSuccess: (data) => {
          console.log("downloaded report", data);
          const link = document.createElement('a');
          link.href = data.data;
          link.setAttribute('download', reportName);
          link.style.display = 'none';
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        },
        onError: () => console.error('Failed to download the report.'),
      }
    );
  };

  const handleOpenBillFile = (fileUrl) => {
    DownloadFile(
      { fileUrl },
      {
        onSuccess: (data) => {
          window.open(data.data, '_blank', 'noopener,noreferrer');
        },
        onError: (error) => message.error(getErrorMessage(error, t('downloadFailed') || 'Failed to open bill file.')),
      }
    );
  };

  const handleDownloadCustomReport = (record) => {
    const fileUrl = record.excelReportPath || record.reportPath;
    if (!fileUrl) return;
    const isExcel = !!record.excelReportPath || String(fileUrl).toLowerCase().endsWith('.xlsx');
    const ext = isExcel ? 'xlsx' : 'pdf';
    const reportName = `${record.name || 'custom-report'}.${ext}`;
    setDownloadingCustomReportId(record._id);
    DownloadFile(
      { fileUrl },
      {
        onSuccess: (data) => {
          const link = document.createElement('a');
          link.href = data.data;
          link.setAttribute('download', reportName);
          link.style.display = 'none';
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        },
        onError: (error) => message.error(getErrorMessage(error, t('downloadFailed') || 'Failed to download report.')),
        onSettled: () => setDownloadingCustomReportId(null),
      }
    );
  };

  const commonColumns = [
    {
      title: t('range'),
      key: 'dateRange',
      align: 'center',
      width: isMobile ? 140 : 180,
      render: (_, record) => {
        const from = record.from != null ? Number(record.from) : null;
        const to = record.to != null ? Number(record.to) : null;
        if (from == null || to == null) return '—';
        try {
          const f = dayjs.unix(from);
          const tDate = dayjs.unix(to);
          if (f.month() === tDate.month() && f.year() === tDate.year()) {
            return `${f.format('DD.MM.YYYY')} - ${tDate.format('DD.MM.YYYY')}`;
          }
          if (f.year() === tDate.year()) {
            return `${f.format('DD.MM.YYYY')} - ${tDate.format('DD.MM.YYYY')}`;
          }
          return `${f.format('DD.MM.YYYY')} - ${tDate.format('DD.MM.YYYY')}`;
        } catch {
          return '—';
        }
      },
    },
    {
      title: t('status'),
      dataIndex: 'status',
      key: 'status',
      align: 'center',
      width: isMobile ? 100 : 110,
      render: (status) => {
        const statusConfig = {
          COMPLETED: { color: 'green' },
          PENDING: { color: 'orange' },
          IN_PROGRESS: { color: 'blue' },
          FAILED: { color: 'red' },
        };
        const config = statusConfig[status] || { color: 'default' };
        return <Tag color={config.color} variant="outlined">{t(status) || status}</Tag>;
      },
    },
    {
      title: t('chargingConsumption'),
      dataIndex: 'totalConsumption',
      key: 'totalConsumption',
      align: 'center',
      width: isMobile ? 120 : 140,
      render: (_, record) => `${formatKwhFromWatts(record?.totalConsumption)} kWh`,
    },
    // Electricity bill columns: show "—" when electricityBillDetails is missing (existing reports unchanged)
    {
      title: t('electricityBillConsumption'),
      key: 'electricityBillConsumption',
      align: 'center',
      width: isMobile ? 120 : 140,
      render: (_, record) => {
        const bill = record?.electricityBillDetails;
        const val = bill?.totalConsumption;
        if (!bill || val == null || val === '' || Number.isNaN(Number(val))) return '—';
        return `${formatReportNumber(val)} kWh`;
      },
    },
    {
      title: t('electricityBillAmount'),
      key: 'electricityBillAmount',
      align: 'center',
      width: isMobile ? 100 : 120,
      render: (_, record) => {
        const bill = record?.electricityBillDetails;
        if (!bill || bill.totalAmount == null || bill.totalAmount === '') return '—';
        return formatReportNumber(bill.totalAmount);
      },
    },
    {
      title: t('perUnitPrice'),
      key: 'electricityBillPerUnitPrice',
      align: 'center',
      width: isMobile ? 100 : 110,
      render: (_, record) => {
        const bill = record?.electricityBillDetails;
        if (!bill || bill.perUnitPrice == null || bill.perUnitPrice === '') return '—';
        return formatReportNumber(bill.perUnitPrice);
      },
    },
    {
      title: t('electricityBillFile'),
      key: 'electricityBillFile',
      align: 'center',
      width: isMobile ? 90 : 100,
      render: (_, record) => {
        const bill = record?.electricityBillDetails;
        const fileUrl = bill?.billFileUrl;
        if (!fileUrl || typeof fileUrl !== 'string') return '—';
        return (
          <Tooltip title={t('downloadReport')}>
            <Button
              variant="outlined"
              icon={<DownloadOutlined />}
              onClick={() => handleOpenBillFile(fileUrl)}
            />
          </Tooltip>
        );
      },
    },
    {
      title: t('reportLink'),
      key: 'reportPath',
      align: 'center',
      width: isMobile ? 100 : 120,
      render: (_, record) => {
        const fileUrl = record.excelReportPath || record.reportPath;
        const isExcel = record.excelReportPath || (record.reportPath && String(record.reportPath).toLowerCase().endsWith('.xlsx'));
        const ext = isExcel ? 'xlsx' : 'pdf';
        if (!fileUrl) return null;
        return (
          <Tooltip title={t('downloadReport')}>
            <Button
              variant="outlined"
              icon={<DownloadOutlined />}
              onClick={() =>
                handleDownloadReport(fileUrl, `${record.name || 'report'}.${ext}`)
              }
            />
          </Tooltip>
        );
      },
    },
  ];

  const columns = [
    {
      title: t('reportName'),
      dataIndex: 'name',
      key: 'name',
      align: 'center',
      width: isMobile ? 120 : 150,
    },
    {
      title: t('operatorName'),
      dataIndex: 'operatorName',
      key: 'operatorName',
      align: 'center',
      width: isMobile ? 120 : 150,
    },
    ...commonColumns,
  ];

  const locationOwnerColumns = [
    {
      title: t('reportName'),
      dataIndex: 'name',
      key: 'name',
      align: 'center',
      width: isMobile ? 120 : 150,
    },
    {
      title: t('locationOwnerName'),
      key: 'locationOwnerName',
      align: 'center',
      width: isMobile ? 140 : 180,
      render: (_, record) => {
        const loc = record.locationDetails;
        if (loc?.locationName) return loc.locationName;
        if (loc?.contactPersonFname != null || loc?.contactPersonLname != null) {
          return `${loc.contactPersonFname || ''} ${loc.contactPersonLname || ''}`.trim() || '—';
        }
        if (record.locationOwnerName) return record.locationOwnerName;
        if (record.teamUserFname != null || record.teamUserLname != null) {
          return `${record.teamUserFname || ''} ${record.teamUserLname || ''}`.trim() || '—';
        }
        return record.locationId || '—';
      },
    },
    ...commonColumns,
  ];

  // eslint-disable-next-line no-unused-vars -- kept if Super Admin tab is restored
  const superAdminColumns = [
    {
      title: t('reportName'),
      dataIndex: 'name',
      key: 'name',
      align: 'center',
      width: isMobile ? 120 : 150,
    },
    ...commonColumns,
  ];

  const residentColumns = [
    {
      title: `${t('resident')}`,
      key: 'reportNameResident',
      align: 'center',
      width: isMobile ? 140 : 200,
      render: (_, record) => {
        const c = record?.customerDetails;
        const residentName = c ? [c.fname, c.lname].filter(Boolean).join(' ').trim() || '—' : '—';
        const label = `${residentName}`;
        const customerId =
          record?.customerId ||
          record?.customerDetails?._id ||
          record?.customerDetails?.id;
        if (customerId) {
          return (
            <Tooltip title={t('viewDetails')}>
              <Button
                type="link"
                variant="outlined"
                className="hover:underline font-medium"
                onClick={() => {
                  setSelectedCustomerIdForDrawer({
                    id: customerId,
                    operatorId: record?.operatorId ?? operatorId,
                  });
                  setCustomerDrawerOpen(true);
                }}
              >
                {label}
              </Button>
            </Tooltip>
          );
        }
        return label;
      },
    },
    {
      title: t('operatorName'),
      dataIndex: 'operatorName',
      key: 'operatorName',
      align: 'center',
      width: isMobile ? 120 : 150,
      render: (name, record) => name || record.operatorId || '—',
    },
    ...commonColumns,
  ];

  const customReportColumns = useMemo(() => [
    {
      title: t('reportName'),
      dataIndex: 'name',
      key: 'name',
      align: 'center',
      width: isMobile ? 150 : 200,
      ellipsis: true,
      render: (text) => (
        <span className="whitespace-nowrap" title={text}>
          {text || '-'}
        </span>
      ),
    },
    {
      title: t('reportTemplate'),
      dataIndex: 'reportTemplate',
      key: 'reportTemplate',
      align: 'center',
      width: isMobile ? 140 : 180,
      render: (template) => (
        <Tag color="blue" className="whitespace-nowrap" variant="outlined">
          {t(template) || template}
        </Tag>
      ),
    },
    {
      title: t('range'),
      key: 'dateRange',
      align: 'center',
      width: isMobile ? 180 : 220,
      render: (_, record) => {
        const from = record.from;
        const to = record.to;
        if (from == null || to == null) return '-';
        try {
          const isUnixFrom = typeof from === 'number' || (typeof from === 'string' && /^\d+$/.test(from));
          const isUnixTo = typeof to === 'number' || (typeof to === 'string' && /^\d+$/.test(to));
          const f = isUnixFrom ? dayjs.unix(Number(from)) : dayjs(from);
          const tDate = isUnixTo ? dayjs.unix(Number(to)) : dayjs(to);
          let text;
          if (f.month() === tDate.month() && f.year() === tDate.year()) {
            text = `${f.format('DD.MM.YYYY')} - ${tDate.format('DD.MM.YYYY')}`;
          } else if (f.year() === tDate.year()) {
            text = `${f.format('DD.MM.YYYY')} - ${tDate.format('DD.MM.YYYY')}`;
          } else {
            text = `${f.format('DD.MM.YYYY')} - ${tDate.format('DD.MM.YYYY')}`;
          }
          return <span className="whitespace-nowrap">{text}</span>;
        } catch {
          return '-';
        }
      },
    },
    {
      title: t('status'),
      dataIndex: 'status',
      key: 'status',
      align: 'center',
      width: isMobile ? 100 : 120,
      render: (status) => {
        const statusConfig = {
          COMPLETED: { color: 'green', icon: <CheckCircleOutlined /> },
          PENDING: { color: 'orange', icon: <ClockCircleOutlined /> },
          IN_PROGRESS: { color: 'blue', icon: <SyncOutlined /> },
          FAILED: { color: 'red', icon: <CloseCircleOutlined /> },
        };
        const config = statusConfig[status] || { color: 'default', icon: null };
        return (
          <Tag color={config.color} icon={config.icon} className="whitespace-nowrap" variant="outlined">
            {t(status) || status}
          </Tag>
        );
      },
    },
    {
      title: t('createdAt'),
      dataIndex: 'created_at',
      key: 'created_at',
      align: 'center',
      width: isMobile ? 140 : 180,
      render: (timestamp) => {
        if (timestamp == null) return '-';
        const ts = Number(timestamp);
        if (Number.isNaN(ts)) return '-';
        try {
          return (
            <span className="whitespace-nowrap">
              {dayjs.unix(ts).format('DD.MM.YYYY HH:mm')}
            </span>
          );
        } catch {
          return '-';
        }
      },
    },
    {
      title: t('action'),
      key: 'action',
      align: 'center',
      width: isMobile ? 100 : 120,
      fixed: 'right',
      render: (_, record) =>
        record.status === 'COMPLETED' && (record.excelReportPath || record.reportPath) ? (
          <Tooltip title={t('downloadReport')}>
            <Button
              variant="outlined"
              icon={<DownloadOutlined />}
              loading={downloadingCustomReportId === record._id}
              onClick={() => handleDownloadCustomReport(record)}
            />
          </Tooltip>
        ) : (
          <span className="text-gray-400">-</span>
        ),
    },
  ], [t, isMobile, downloadingCustomReportId, handleDownloadCustomReport]);

  // Main tab items
  const mainTabItems = useMemo(() => {
    const items = [];

    items.push({
      key: 'reports',
      label: (
        <span>
          <FileTextOutlined style={{ fontSize: '16px', marginRight: '8px' }} />
          {t('reports')}
        </span>
      ),
      children: (
        <Row gutter={18} className="">
          <Col span={24}>
            {/* MonthPicker and Create report for mobile (Create only on Company tab) */}
            {isMobile && (
              <div className="mb-4 flex gap-2 items-center">
                <MonthPicker
                  onChange={handleMonthChange}
                  placeholder={t('selectMonth')}
                  value={selectedMonth ? dayjs(selectedMonth) : null}
                  className="flex-1"
                />
                {activeReportTab === 'OPERATOR' && (
                  <Button
                    variant="outlined"
                    type="primary"
                    icon={<PlusOutlined />}
                    onClick={handleOpenCreateReport}
                    className="border-blue-600 text-blue-600 hover:bg-blue-50 hover:border-blue-700 hover:text-blue-700"
                  >
                    {t('createNewReport')}
                  </Button>
                )}
              </div>
            )}

            <Tabs
              activeKey={activeReportTab}
              tabBarExtraContent={!isMobile ? (
                <div className="flex flex-wrap items-center gap-2 mb-2">
                  <MonthPicker
                    onChange={handleMonthChange}
                    placeholder={t('selectMonth')}
                    value={selectedMonth ? dayjs(selectedMonth) : null}
                    size="middle"
                    className="max-w-sm"
                  />
                  {activeReportTab === 'LOCATION_OWNER' && (
                    <>
                      <DebouncedSelect
                        value={reportLocationId}
                        onChange={handleReportLocationChange}
                        allowClear
                        className="min-w-[200px]"
                        disabled={!operatorId}
                        fetchOptions={(search) => (operatorId ? fetchLocationList({ operatorId, search }) : Promise.resolve([]))}
                        placeholder={operatorId ? t('searchLocation') : t('selectOperatorFirst')}
                        loadOnOpen
                      />
                    </>
                  )}
                  {activeReportTab === 'CUSTOMER' && (
                    <>
                      <DebouncedSelect
                        value={reportCustomerId}
                        onChange={setReportCustomerId}
                        allowClear
                        className="min-w-[200px]"
                        disabled={!operatorId}
                        fetchOptions={(search) => (operatorId ? fetchCustomerList({ operatorId, search }) : Promise.resolve([]))}
                        placeholder={operatorId ? t('searchCustomer') : t('selectOperatorFirst')}
                        loadOnOpen
                      />
                      {reportCustomerId && (
                        <Button
                          variant="outlined"
                          size="middle"
                          onClick={() => setReportCustomerId(null)}
                        >
                          {t('clear')}
                        </Button>
                      )}
                      {['SUPER_ADMIN', 'OPERATOR'].includes(user?.role) && (
                        <Button
                          type="primary"
                          size="middle"
                          loading={isGeneratingCustomerPdf}
                          disabled={!reportCustomerId || !selectedMonth}
                          onClick={handleGenerateCustomerPdf}
                        >
                          {t('generateCustomerPdf')}
                        </Button>
                      )}
                    </>
                  )}
                  {activeReportTab === 'OPERATOR' && (
                    <Button
                      variant="outlined"
                      type="primary"
                      icon={<PlusOutlined />}
                      onClick={handleOpenCreateReport}
                      className="border-blue-600 text-blue-600 hover:bg-blue-50 hover:border-blue-700 hover:text-blue-700"
                    >
                      {t('createNewReport')}
                    </Button>
                  )}
                </div>
              ) : undefined}
              onChange={handleReportsTabChange}
              type="card"
              items={[
                ...(['SUPER_ADMIN', 'ADMIN', 'OPERATOR'].includes(user?.role)
                  ? [
                    {
                      key: 'OPERATOR',
                      label: (
                        <span>
                          <TeamOutlined style={{ fontSize: '16px', marginRight: '8px' }} />
                          {t('operators')}
                        </span>
                      ),
                      children: (
                        <>
                          <Table
                            columns={columns}
                            dataSource={reportList?.paginatedData}
                            loading={isReportListLoading}
                            rowKey="_id"
                            pagination={{
                              ...pagination,
                              pageSize: isMobile ? 5 : pagination.pageSize,
                              position: ['bottomCenter'],
                              total: reportList?.totalCount || 0,
                              size: isMobile ? 'small' : 'default',
                            }}
                            onChange={handleTableChange}
                            scroll={{ x: isMobile ? 800 : undefined }}
                          />
                        </>
                      ),
                    },
                  ]
                  : []),
                {
                  key: 'LOCATION_OWNER',
                  label: (
                    <span>
                      <ShopOutlined style={{ fontSize: '16px', marginRight: '8px' }} />
                      {t('locationOwners')}
                    </span>
                  ),
                  children: (
                    <>
                      {isMobile && activeReportTab === 'LOCATION_OWNER' && (
                        <div className="mb-4 flex flex-wrap items-center gap-2">
                          <DebouncedSelect
                            value={reportLocationId}
                            onChange={handleReportLocationChange}
                            allowClear
                            className="w-full min-w-0"
                            disabled={!operatorId}
                            fetchOptions={(search) => (operatorId ? fetchLocationList({ operatorId, search }) : Promise.resolve([]))}
                            placeholder={operatorId ? t('searchLocation') : t('selectOperatorFirst')}
                            loadOnOpen
                          />
                          {reportLocationId && (
                            <Button
                              variant="outlined"
                              size="middle"
                              onClick={() => handleReportLocationChange(null)}
                            >
                              {t('clear')}
                            </Button>
                          )}
                        </div>
                      )}
                      <Table
                        columns={locationOwnerColumns}
                        dataSource={reportList?.paginatedData}
                        loading={isReportListLoading}
                        rowKey="_id"
                        pagination={{
                          ...pagination,
                          pageSize: isMobile ? 5 : pagination.pageSize,
                          position: ['bottomCenter'],
                          total: reportList?.totalCount || 0,
                          size: isMobile ? 'small' : 'default',
                        }}
                        onChange={handleTableChange}
                        scroll={{ x: isMobile ? 800 : undefined }}
                      />
                    </>
                  ),
                },
                {
                  key: 'CUSTOMER',
                  label: (
                    <span>
                      <UserOutlined style={{ fontSize: '16px', marginRight: '8px' }} />
                      {t('residentUtilizationReport')}
                    </span>
                  ),
                  children: (
                    <>
                      {isMobile && activeReportTab === 'CUSTOMER' && (
                        <div className="mb-4 flex flex-wrap items-center gap-2">
                          <DebouncedSelect
                            value={reportCustomerId}
                            onChange={setReportCustomerId}
                            allowClear
                            className="w-full min-w-0"
                            disabled={!operatorId}
                            fetchOptions={(search) => (operatorId ? fetchCustomerList({ operatorId, search }) : Promise.resolve([]))}
                            placeholder={operatorId ? t('searchCustomer') : t('selectOperatorFirst')}
                            loadOnOpen
                          />
                          {reportCustomerId && (
                            <Button
                              variant="outlined"
                              size="middle"
                              onClick={() => setReportCustomerId(null)}
                            >
                              {t('clear')}
                            </Button>
                          )}
                        </div>
                      )}
                      <Table
                        columns={residentColumns}
                        dataSource={reportList?.paginatedData}
                        loading={isReportListLoading}
                        rowKey="_id"
                        pagination={{
                          ...pagination,
                          pageSize: isMobile ? 5 : pagination.pageSize,
                          position: ['bottomCenter'],
                          total: reportList?.totalCount || 0,
                          size: isMobile ? 'small' : 'default',
                        }}
                        onChange={handleTableChange}
                        scroll={{ x: isMobile ? 800 : undefined }}
                      />
                    </>
                  ),
                },
              ]}
            />
          </Col>
        </Row>
      ),
    });

    if (user?.role !== 'LOCATION_OWNER') {
      items.push({
        key: 'otherReports',
        label: (
          <span>
            <LineChartOutlined style={{ fontSize: '16px', marginRight: '8px' }} />
            {t('otherReports')}
          </span>
        ),
        children: (
          <>
            <Tabs
              activeKey={activeOtherReportTab}
              onChange={handleOtherReportsTabChange}
              type="card"
              size="small"
              items={[
                {
                  key: 'chargePointCount',
                  label: (
                    <span>
                      <ThunderboltOutlined style={{ fontSize: '16px', marginRight: '8px' }} />
                      {t('chargePointCount')}
                    </span>
                  ),
                  children: (
                    <ChargePointCountTab
                      operatorId={operatorId}
                      active={activeMainTab === 'otherReports' && activeOtherReportTab === 'chargePointCount'}
                    />
                  ),
                },
                {
                  key: 'chargingSession',
                  label: (
                    <span>
                      <PoweroffOutlined style={{ fontSize: '16px', marginRight: '8px' }} />
                      {t('chargingSession')}
                    </span>
                  ),
                  children: (
                    <ChargingSessionTab
                      operatorId={operatorId}
                      active={activeMainTab === 'otherReports' && activeOtherReportTab === 'chargingSession'}
                    />
                  ),
                },
                {
                  key: 'energyConsumption',
                  label: (
                    <span>
                      <LineChartOutlined style={{ fontSize: '16px', marginRight: '8px' }} />
                      {t('energyConsumption')}
                    </span>
                  ),
                  children: (
                    <EnergyConsumptionTab
                      operatorId={operatorId}
                      active={activeMainTab === 'otherReports' && activeOtherReportTab === 'energyConsumption'}
                    />
                  ),
                },
                {
                  key: 'chargerOnlineTime',
                  label: (
                    <span>
                      <ClockCircleOutlined style={{ fontSize: '16px', marginRight: '8px' }} />
                      {t('chargerOnlineTime')}
                    </span>
                  ),
                  children: (
                    <ChargerOnlineTimeTab
                      operatorId={operatorId}
                      operatorName={operatorName}
                      active={activeMainTab === 'otherReports' && activeOtherReportTab === 'chargerOnlineTime'}
                    />
                  ),
                },
                {
                  key: 'revenue',
                  label: (
                    <span>
                      <DollarOutlined style={{ fontSize: '16px', marginRight: '8px' }} />
                      {t('revenue')}
                    </span>
                  ),
                  children: (
                    <RevenueTab
                      operatorId={operatorId}
                      name={operatorName}
                      active={activeMainTab === 'otherReports' && activeOtherReportTab === 'revenue'}
                    />
                  ),
                },
                {
                  key: 'userCount',
                  label: (
                    <span>
                      <UserOutlined style={{ fontSize: '16px', marginRight: '8px' }} />
                      {t('userCount')}
                    </span>
                  ),
                  children: (
                    <UserCountTab
                      operatorId={operatorId}
                      active={activeMainTab === 'otherReports' && activeOtherReportTab === 'userCount'}
                    />
                  ),
                },
                {
                  key: 'userAnalysis',
                  label: (
                    <span>
                      <UserSwitchOutlined style={{ fontSize: '16px', marginRight: '8px' }} />
                      {t('userAnalysis')}
                    </span>
                  ),
                  children: (
                    <UserAnalysisTab
                      active={activeMainTab === 'otherReports' && activeOtherReportTab === 'userAnalysis'}
                    />
                  ),
                },
              ]}
            />
          </>
        ),
      });
    }

    items.push({
      key: 'customReports',
      label: (
        <span>
          <FileTextOutlined style={{ fontSize: '16px', marginRight: '8px' }} />
          {t('customReports')}
        </span>
      ),
      children: (
        <div className="custom-reports-table-wrapper space-y-4">
          <div className="flex flex-wrap gap-4 items-center">
            <RangePicker
              value={customReportDateRange}
              onChange={setCustomReportDateRange}
              placeholder={[t('startDate'), t('endDate')]}
              style={{ width: 250 }}
            />
            <Select
              placeholder={t('selectStatus')}
              style={{ width: 150 }}
              allowClear
              value={customReportStatus}
              onChange={setCustomReportStatus}
            >
              <Option value="PENDING">{t('PENDING')}</Option>
              <Option value="IN_PROGRESS">{t('IN_PROGRESS')}</Option>
              <Option value="COMPLETED">{t('COMPLETED')}</Option>
              <Option value="FAILED">{t('FAILED')}</Option>
            </Select>
            <Select
              placeholder={t('selectTemplate')}
              style={{ width: 200 }}
              allowClear
              value={customReportTemplate}
              onChange={setCustomReportTemplate}
            >
              <Option value="CHARGING_HISTORY">{t('CHARGING_HISTORY')}</Option>
              <Option value="CHARGING_SESSION">{t('CHARGING_SESSION')}</Option>
              <Option value="RFID_USAGE_REPORT">{t('RFID_USAGE_REPORT')}</Option>
            </Select>
            <Button
              variant="outlined"
              icon={<ReloadOutlined />}
              onClick={refetchCustomReportList}
              loading={isCustomReportListLoading}
            >
              {t('Refresh')}
            </Button>
          </div>
          <style>{`
            .custom-reports-table-wrapper .ant-pagination {
              display: flex !important;
              justify-content: center !important;
              align-items: center !important;
              flex-wrap: nowrap !important;
              gap: 8px !important;
            }
            .custom-reports-table-wrapper .ant-pagination > * {
              flex-shrink: 0 !important;
            }
          `}</style>
          <Table
            columns={customReportColumns}
            dataSource={customReportList?.paginatedData || []}
            loading={isCustomReportListLoading}
            rowKey="_id"
            variant="outlined"
            size={isMobile ? 'small' : 'default'}
            scroll={{ x: isMobile ? 800 : 'max-content' }}
            pagination={{
              current: customReportPagination.current,
              pageSize: customReportPagination.pageSize,
              total: customReportList?.totalCount || 0,
              showSizeChanger: true,
              pageSizeOptions: ['5', '10', '20', '50'],
              position: ['bottomCenter'],
              size: 'small',
              showTotal: false,
              onChange: (page, pageSize) => {
                setCustomReportPagination({
                  current: page,
                  pageSize: pageSize,
                  total: customReportList?.totalCount || 0,
                });
              },
              onShowSizeChange: (current, size) => {
                setCustomReportPagination({
                  current: 1,
                  pageSize: size,
                  total: customReportList?.totalCount || 0,
                });
              },
            }}
            locale={{
              emptyText: (
                <Empty
                  description={t('noCustomReports')}
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                />
              ),
            }}
          />
        </div>
      ),
    });

    return items;
  }, [user?.role, t, activeReportTab, operatorId, operatorName, commonColumns, reportList?.paginatedData, reportList?.totalCount, isReportListLoading, pagination, columns, locationOwnerColumns, activeOtherReportTab, activeMainTab, customReportList, isCustomReportListLoading, customReportPagination, isMobile, customReportDateRange, customReportStatus, customReportTemplate]);

  // Handle navigation state
  useEffect(() => {
    if (location.state?.activeMainTab) {
      setActiveMainTab(location.state.activeMainTab);
    }
    if (location.state?.activeOtherReportTab) {
      setActiveOtherReportTab(location.state.activeOtherReportTab);
    }
  }, [location.state]);

  return (
    <div className="space-y-4">
      <Tabs
        activeKey={activeMainTab}
        onChange={handleMainTabChange}
        type="card"
        tabBarExtraContent={
          // Remove extra reload button as it is now integrated in the tab content
          undefined

        }
        items={mainTabItems}
      />

      <Drawer
        title={t('createNewReport')}
        placement="right"
        styles={{
          wrapper: {
            width: Math.min(600, typeof window !== 'undefined' ? window.innerWidth * 0.9 : 600),
          },
        }}
        onClose={handleCloseCreateReportDrawer}
        open={createReportDrawerOpen}
        destroyOnClose
        footer={
          <div className="flex justify-start gap-2">
             <Button
              variant="outlined"
              type="primary"
              loading={isGeneratingReport}
              onClick={() => createReportForm.submit()}
            >
              {t('createNewReport')}
            </Button>
            <Button variant="outlined" onClick={handleCloseCreateReportDrawer}>
              {t('cancel')}
            </Button>
           
          </div>
        }
      >
        <Form
          form={createReportForm}
          layout="vertical"
          onFinish={handleCreateReportSubmit}
          className="pt-2"
        >
          {/* operatorId injected from store on submit (single-tenant) */}
          <Form.Item label={t('perUnitPrice')}>
            <TouRatesHint operatorId={operatorId} />
          </Form.Item>
          <Form.Item
            name="perUnitPrice"
            label={t('perUnitPriceOverride') || 'Override unit price (optional)'}
          >
            <InputNumber min={0} step={0.01} className="!max-w-[200px] !w-full !h-[38.4px]" placeholder={t('placeholderPerUnitPrice')} />
          </Form.Item>
          <Form.Item
            name="fullBillAmount"
            label={t('fullBillAmount')}
            rules={[{ required: true, message: t('fieldRequired') }]}
          >
            <InputNumber min={0} step={0.01} className="!max-w-[200px] !w-full !h-[38.4px]" placeholder={t('placeholderFullBillAmount')} />
          </Form.Item>
          <Form.Item label={t('billFileUrl')}>
            <Upload
              listType="picture-card"
              fileList={createReportBillFileList}
              maxCount={1}
              customRequest={handleBillFileUpload}
              showUploadList
              onRemove={() => {
                setCreateReportUploadedBillUrl('');
                setCreateReportBillFileList([]);
              }}
              onChange={({ fileList }) => setCreateReportBillFileList(fileList.slice(-1))}
              accept=".pdf,.xlsx,.xls,.doc,.docx,image/*"
            >
              {createReportBillFileList.length < 1 && (
                <button type="button" className="flex flex-col gap-2 items-center justify-center w-full h-full min-h-[40px]">
                  <UploadOutlined style={{ fontSize: 20 }} />
                  <span>{t('uploadBillFile')}</span>
                </button>
              )}
            </Upload>
          </Form.Item>
          <Form.Item
            name="totalConsumption"
            label={t('totalConsumption')}
            rules={[{ required: true, message: t('fieldRequired') }]}
          >
            <InputNumber min={0} step={0.01} className="!max-w-[200px] !w-full !h-[38.4px]" placeholder={t('placeholderTotalConsumption')} />
          </Form.Item>
        </Form>
      </Drawer>

      {customerDrawerOpen && selectedCustomerIdForDrawer?.id && (
        <CustomerDrawerView
          open={customerDrawerOpen}
          onClose={() => {
            setCustomerDrawerOpen(false);
            setSelectedCustomerIdForDrawer(null);
          }}
          id={selectedCustomerIdForDrawer.id}
          operatorId={selectedCustomerIdForDrawer.operatorId}
        />
      )}
    </div>
  );
}
